/**
 * Bar-chart-race engine (pure, time-driven).
 *
 * `computeRaceFrame(timeline, t)` is a pure function of the elapsed time `t`
 * (ms): given the per-round standings, it returns the fully-interpolated frame
 * (row positions, bar widths, count-up point values, enter/exit opacity). The
 * SAME function drives in-app rAF playback (t = clock) and server-side video
 * capture (t = seek). What you watch is exactly what the video shows.
 *
 * No React, no DOM — just math, so it can run in the browser, in the capture
 * page, and in tests unchanged.
 */

import type { RankingTimelineData, RankingTimelineStanding } from '../types';

// Timing (ms).
export const INTRO_MS = 1500;
export const OUTRO_MS = 2500;
const RACE_BUDGET_MS = 20000; // keeps total ≤ ~24s (WhatsApp-status-friendly)
const ROUND_MIN_MS = 800;
const ROUND_MAX_MS = 1600;

/** How many rows stay fully visible; rows below this fade out the bottom edge. */
export const TOP_N = 10;

export interface RaceRow {
  playerId: string;
  /** Fractional 0-based row index (multiply by row height for pixel y). */
  y: number;
  /** Bar fill 0..100 (fixed global scale, 4% floor for ≤0 values). */
  widthPct: number;
  /** Count-up point value for the label (already rounded). */
  displayPoints: number;
  /** 0..1, folds together enter animation and top-N window fade. */
  opacity: number;
  /** Integer row slot (1-based) — what the layout follows. */
  position: number;
  /** Standings position for the badge; tied players share it. */
  rank: number;
}

export interface RaceFrame {
  phase: 'intro' | 'race' | 'outro';
  roundLabel: string;
  roundNumber: number;
  totalRounds: number;
  rows: RaceRow[];
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** Per-round ms, scaled down as the number of rounds grows. */
function roundMs(numRounds: number): number {
  if (numRounds <= 0) return ROUND_MAX_MS;
  return clamp(RACE_BUDGET_MS / numRounds, ROUND_MIN_MS, ROUND_MAX_MS);
}

/** Total playback duration for a timeline (intro + race + outro). */
export function raceDuration(timeline: RankingTimelineData): number {
  const n = timeline.rounds.length;
  if (n === 0) return INTRO_MS + OUTRO_MS;
  return INTRO_MS + roundMs(n) * n + OUTRO_MS;
}

type StandingMap = Map<string, { points: number; position: number; rank: number }>;

function toMap(standings: RankingTimelineStanding[]): StandingMap {
  const m: StandingMap = new Map();
  for (const s of standings) m.set(s.playerId, { points: s.points, position: s.position, rank: s.rank ?? s.position });
  return m;
}

/** Same players/positions as a round but with points zeroed — the "starting grid". */
function zeroed(standings: RankingTimelineStanding[]): StandingMap {
  const m: StandingMap = new Map();
  for (const s of standings) m.set(s.playerId, { points: 0, position: s.position, rank: s.rank ?? s.position });
  return m;
}

interface Scale {
  xMin: number;
  xMax: number;
}

function globalScale(timeline: RankingTimelineData): Scale {
  let xMax = 1;
  let xMin = 0;
  for (const round of timeline.rounds) {
    for (const s of round.standings) {
      if (s.points > xMax) xMax = s.points;
      if (s.points < xMin) xMin = s.points;
    }
  }
  return { xMin, xMax };
}

function widthPct(points: number, scale: Scale): number {
  const range = scale.xMax - scale.xMin || 1;
  const raw = ((points - scale.xMin) / range) * 100;
  return clamp(Math.max(4, raw), 4, 100);
}

const ENTER_ROW = TOP_N + 2; // off-screen slot new bars slide up from

function buildRows(
  playerIds: string[],
  from: StandingMap,
  to: StandingMap,
  eased: number,
  scale: Scale,
): RaceRow[] {
  const rows: RaceRow[] = [];

  for (const id of playerIds) {
    const f = from.get(id);
    const t = to.get(id);
    if (!f && !t) continue; // not yet entered in either frame

    const fromPos = f ? f.position : ENTER_ROW;
    const toPos = t ? t.position : ENTER_ROW;
    const fromPoints = f ? f.points : 0;
    const toPoints = t ? t.points : fromPoints;
    const fromEnter = f ? 1 : 0;
    const toEnter = t ? 1 : 0;

    const pos = lerp(fromPos, toPos, eased);
    const points = lerp(fromPoints, toPoints, eased);
    const enterOpacity = lerp(fromEnter, toEnter, eased);

    // Top-N window: fully visible ≤ TOP_N, fades to 0 by TOP_N + 1.5.
    const windowOpacity = clamp((TOP_N + 1.5 - pos) / 1.5, 0, 1);
    const opacity = enterOpacity * windowOpacity;

    // The badge switches to the destination rank halfway through the move,
    // when the bar has visibly left its old row.
    const fromRank = f ? f.rank : toPos;
    const toRank = t ? t.rank : fromRank;

    rows.push({
      playerId: id,
      y: pos - 1,
      widthPct: widthPct(points, scale),
      displayPoints: Math.round(points),
      opacity,
      position: Math.max(1, Math.round(pos)),
      rank: Math.max(1, eased < 0.5 ? fromRank : toRank),
    });
  }

  return rows;
}

/**
 * Compute the race frame at time `t` (ms). Pure — same input, same output.
 */
export function computeRaceFrame(timeline: RankingTimelineData, tMs: number): RaceFrame {
  const rounds = timeline.rounds;
  const playerIds = timeline.players.map((p) => p.id);
  const scale = globalScale(timeline);
  const totalRounds = rounds.length;
  const maxRoundNumber = rounds.reduce((mx, r) => Math.max(mx, r.roundNumber), totalRounds);

  const label = (n: number) => `Rodada ${n} de ${maxRoundNumber}`;

  if (totalRounds === 0) {
    return { phase: 'intro', roundLabel: '', roundNumber: 0, totalRounds: 0, rows: [] };
  }

  const rMs = roundMs(totalRounds);
  const raceMs = rMs * totalRounds;
  const t = clamp(tMs, 0, raceDuration(timeline));

  // Intro: static starting grid (round 1 at 0 points).
  if (t < INTRO_MS) {
    const grid = zeroed(rounds[0].standings);
    return {
      phase: 'intro',
      roundLabel: label(rounds[0].roundNumber),
      roundNumber: rounds[0].roundNumber,
      totalRounds,
      rows: buildRows(playerIds, grid, grid, 0, scale),
    };
  }

  // Outro: hold final standings.
  if (t >= INTRO_MS + raceMs) {
    const last = toMap(rounds[totalRounds - 1].standings);
    return {
      phase: 'outro',
      roundLabel: label(rounds[totalRounds - 1].roundNumber),
      roundNumber: rounds[totalRounds - 1].roundNumber,
      totalRounds,
      rows: buildRows(playerIds, last, last, 0, scale),
    };
  }

  // Race: N steps, one per round. Step 0 grows from the zeroed grid into round 1.
  const raceProgress = (t - INTRO_MS) / rMs; // 0..N
  const step = clamp(Math.floor(raceProgress), 0, totalRounds - 1);
  const localT = clamp(raceProgress - step, 0, 1);
  const eased = easeInOutCubic(localT);

  const fromMap = step === 0 ? zeroed(rounds[0].standings) : toMap(rounds[step - 1].standings);
  const toRound = rounds[step];
  const to = toMap(toRound.standings);

  return {
    phase: 'race',
    roundLabel: label(toRound.roundNumber),
    roundNumber: toRound.roundNumber,
    totalRounds,
    rows: buildRows(playerIds, fromMap, to, eased, scale),
  };
}
